import {
  Box,
  CircularProgress,
  FormControl,
  MenuItem,
  Modal,
  Select,
} from "@mui/material";
import "./CreateBrand.scss";
import ProtectedComponent from "../../../../components/ProtectedComponent";
import NavigationLayout from "../../navigation";
import BrandProfileLogo from "./BrandProfileLogo";
import ThemePreferences from "./ThemePreferences";
import { BrandApi } from "../../../../utils/api/Brand";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useEffect, useRef, useState } from "react";
import Upload from "../../../../components/icons/Upload";
import SearchIcon from "../../../../components/icons/SearchIcon";
import TrueIcon from "../../../../components/icons/TrueIcon";
import BackArrowIcon from "../../../../components/icons/BackArrowIcon";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import ThemeErrorSummary from "./ThemeErrorSummary";
import FontSelectorModal, { FontItem } from "../FontSelector/FontSelectorModal";
import ErrorSummary from "./ErrorSummary";

const CreateBrand = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [showMobilePreview, setShowMobilePreview] = useState(false);
  const [isFontUploading, setIsFontUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [systemFonts, setSystemFonts] = useState<any[]>([]);
  const [customFonts, setCustomFonts] = useState<any[]>([]);
  const [brandId, setBrandId] = useState<string | null>(null);
  const customFontSectionLabel = "Brand fonts";
  const navigate = useNavigate();

  const [openTitleFontModal, setOpenTitleFontModal] = useState(false);
  const [openSubtitleFontModal, setOpenSubtitleFontModal] = useState(false);
  const [openBodyFontModal, setOpenBodyFontModal] = useState(false);

  const [contrastWarnings, setContrastWarnings] = useState<
    Record<string, string>
  >({});

  const [searchParams, setSearchParams] = useSearchParams();
  const urlBrandId = searchParams.get("id");

  const [isFontProcessing, setIsFontProcessing] = useState(false);

  const [originalColors, setOriginalColors] = useState({
    base_color_primary: null,
    base_color_secondary: null,
    text_color_primary: null,
    text_color_secondary: null,
  });

  const CDN_BASE_URL = process.env.REACT_APP_CDN_BASE_URL || "";

  const buildCdnUrl = (storageKey: string): string => {
    if (!storageKey) return "";
    return `${CDN_BASE_URL.replace(/\/$/, "")}/${storageKey.replace(/^\//, "")}`;
  };

  useEffect(() => {
    if (urlBrandId) {
      setBrandId(urlBrandId);
      formik.setFieldValue("brand_id", urlBrandId);
    }
  }, [urlBrandId]);

  useEffect(() => {
    const loadEverything = async () => {
      if (!brandId) {
        // await fetchSystemFonts();
        return;
      }

      const [brandRes] = await Promise.all([
        BrandApi.getBrandById(brandId),
        // fetchSystemFonts(),
        fetchCustomFonts(brandId),
      ]);

      if (brandRes?.data?.success) {
        const brand = brandRes.data.data;
        const brandLogoRecord = brand.brand_logo;

        setOriginalColors({
          base_color_primary:
            brand.base_color_primary ||
            brand.theme_colors_response?.[0] ||
            null,
          base_color_secondary:
            brand.base_color_secondary ||
            brand.theme_colors_response?.[1] ||
            null,
          text_color_primary:
            brand.text_color_primary ||
            brand.theme_colors_response?.[2] ||
            null,
          text_color_secondary: brand.text_color_secondary || {
            r: 255,
            g: 255,
            b: 255,
            hex: "#FFFFFF",
          },
        });

        if (brand.brand_name) {
          formik.setFieldValue("brand_name", brand.brand_name);
        }

        if (brand.brand_country) {
          formik.setFieldValue("brand_country", brand.brand_country);
        }

        formik.setFieldValue(
          "brand_logo",
          brandLogoRecord?._id || brandLogoRecord?.original_file_path || null,
        );
        formik.setFieldValue("brandLogoUploadRecord", brandLogoRecord || null);

        if (brand.theme_colors_response?.length > 0) {
          formik.setFieldValue(
            "theme_colors",
            brand.theme_colors_response,
            true,
          );
        }
        formik.setFieldValue(
          "theme_colors_response",
          brand.theme_colors_response || [],
        );

        if (brand?.base_color_primary) {
          formik.setFieldValue("base_color_primary", brand.base_color_primary);
        }

        if (brand?.base_color_secondary) {
          formik.setFieldValue(
            "base_color_secondary",
            brand.base_color_secondary,
          );
        }

        if (brand?.text_color_primary) {
          formik.setFieldValue("text_color_primary", brand.text_color_primary);
        }

        if (brand?.text_color_secondary) {
          formik.setFieldValue(
            "text_color_secondary",
            brand.text_color_secondary,
          );
        }

        await formik.validateForm();
      }
    };

    loadEverything();
  }, [brandId]);

  const fetchCustomFonts = async (currentBrandId: string | null = brandId) => {
    if (!currentBrandId) return;

    try {
      const params = {
        fonts_for: "brand" as const,
        brand_id: currentBrandId,
      };

      const res = await BrandApi.GetFonts(params);

      if (!res.success || !res.data) {
        setCustomFonts([]);
        return;
      }

      let fontsArray: any[] = [];
      if (Array.isArray(res.data)) {
        fontsArray = res.data;
      } else if (Array.isArray(res.data?.docs)) {
        fontsArray = res.data.docs;
      } else if (Array.isArray(res.data?.data?.docs)) {
        fontsArray = res.data.data.docs;
      } else if (Array.isArray(res.data?.data)) {
        fontsArray = res.data.data;
      }

      console.log("📦 Brand fonts response:", res.data);
      console.log("📦 Extracted fonts count:", fontsArray.length);

      const cdnBaseUrl = (process.env.REACT_APP_CDN_BASE_URL || "").replace(
        /\/$/,
        "",
      );

      const variantFontsForInjection: any[] = [];
      fontsArray.forEach((font: any) => {
        variantFontsForInjection.push(font);
        if (Array.isArray(font.variants)) {
          font.variants.forEach((v: any) => {
            variantFontsForInjection.push({
              ...v,
              font_family: font.font_family,
            });
          });
        }
      });

      let fontStyles = "";
      variantFontsForInjection.forEach((font: any) => {
        const key = (font.woff2_font_storage_key || "").trim();
        if (!key) return;
        const storageKey = key.replace(/^\//, "");
        const fullUrl = `${cdnBaseUrl}/${storageKey}`;

        fontStyles += `
        @font-face {
          font-family: "${font.font_family}";
          src: url("${fullUrl}") format("woff2");
          font-weight: ${font.font_weight || "400"};
          font-style: ${font.is_italic ? "italic" : "normal"};
          font-display: swap;
        }
      `;
      });

      let styleTag = document.getElementById(
        "custom-brand-fonts",
      ) as HTMLStyleElement;
      if (!styleTag) {
        styleTag = document.createElement("style");
        styleTag.id = "custom-brand-fonts";
        document.head.appendChild(styleTag);
      }
      styleTag.innerHTML = fontStyles;

      const mapped = fontsArray.map((font: any) => ({
        label: font.font_family,
        value: font.default_variant_id || font._id,
        family: font.font_family,
        fontWeight: font.font_weight || "400",
        fontName: font.font_name,
      }));

      setCustomFonts(mapped);

      const uploadedList = fontsArray.map((font: any) => {
        let availableWeights: string[] = [];

        if (
          Array.isArray(font.available_weights) &&
          font.available_weights.length > 0
        ) {
          availableWeights = font.available_weights;
        } else if (Array.isArray(font.variants) && font.variants.length > 0) {
          availableWeights = font.variants.map((v: any) =>
            v.is_italic ? `${v.font_weight} Italic` : v.font_weight,
          );
        } else {
          availableWeights = [
            font.is_italic
              ? `${font.font_weight} Italic`
              : font.font_weight || "400",
          ];
        }

        return {
          id: font._id,
          family: font.font_family,
          availableWeights,
          totalVariants: font.total_variants || availableWeights.length,
        };
      });

      formik.setFieldValue("brandFonts", uploadedList);

      const validCustomValues = mapped.map((f: any) => f.value);
      const allValidFonts = [...validCustomValues, "design_default"];

      const fontFields = [
        "title_fonts",
        "subtitle_fonts",
        "body_text_fonts",
      ] as const;
      fontFields.forEach((field) => {
        const currentValue = formik.values[field] as string;
        if (!allValidFonts.includes(currentValue)) {
          formik.setFieldValue(field, "design_default");
        }
      });
    } catch (error) {
      console.error("Error fetching custom fonts:", error);
    }
  };

  // const fetchSystemFonts = async () => {
  //   try {
  //     const res = await BrandApi.ListSystemFonts({ page: 1, limit: 100 });

  //     if (res.success && res.data?.data?.docs) {
  //       const fontsArray = res.data.data.docs;
  //       const cdnBaseUrl = (process.env.REACT_APP_CDN_BASE_URL || "").replace(
  //         /\/$/,
  //         "",
  //       );

  //       const validFonts = fontsArray.filter(
  //         (font: any) =>
  //           font.woff2_font_storage_key &&
  //           font.woff2_font_storage_key.trim() !== "",
  //       );

  //       let fontStyles = "";

  //       validFonts.forEach((font: any) => {
  //         const storageKey = font.woff2_font_storage_key.replace(/^\//, "");
  //         const fullUrl = `${cdnBaseUrl}/${storageKey}`;

  //         console.log("System Font URL:", fullUrl);

  //         fontStyles += `
  //         @font-face {
  //           font-family: "${font.font_family}";
  //           src: url("${fullUrl}") format("woff2");
  //           font-weight: ${font.font_weight || "400"};
  //           font-style: ${font.is_italic ? "italic" : "normal"};
  //           font-display: swap;
  //         }
  //       `;
  //       });

  //       let styleTag = document.getElementById(
  //         "system-fonts-style",
  //       ) as HTMLStyleElement;
  //       if (!styleTag) {
  //         styleTag = document.createElement("style");
  //         styleTag.id = "system-fonts-style";
  //         document.head.appendChild(styleTag);
  //       }
  //       styleTag.innerHTML = fontStyles;

  //       const mapped = validFonts.map((font: any) => ({
  //         label: font.font_family,
  //         value: font.default_variant_id || font._id,
  //         family: font.font_family,
  //         fontWeight: font.font_weight || "400",
  //         fontName: font.font_name,
  //       }));

  //       setSystemFonts(mapped);
  //     }
  //   } catch (error) {
  //     console.error("Error fetching system fonts:", error);
  //     toast.error("Something went wrong while loading system fonts");
  //   }
  // };

  const pollForFontReady = async (
    currentBrandId: string,
    uploadedFontIds: string[],
    maxAttempts: number = 15,
    delayMs: number = 2000,
  ): Promise<boolean> => {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      console.log(`🔄 Polling attempt ${attempt}/${maxAttempts}...`);

      try {
        const res = await BrandApi.GetFonts({
          fonts_for: "brand" as const,
          brand_id: currentBrandId,
        });

        if (res.success && res.data) {
          let fontsArray: any[] = [];
          if (Array.isArray(res.data)) {
            fontsArray = res.data;
          } else if (Array.isArray(res.data?.docs)) {
            fontsArray = res.data.docs;
          } else if (Array.isArray(res.data?.data?.docs)) {
            fontsArray = res.data.data.docs;
          } else if (Array.isArray(res.data?.data)) {
            fontsArray = res.data.data;
          }

          console.log("📦 Polling fonts array:", fontsArray);
          console.log("📦 Looking for IDs:", uploadedFontIds);

          const readyFonts = fontsArray.filter(
            (font: any) =>
              font.woff2_font_storage_key &&
              font.woff2_font_storage_key.trim() !== "",
          );

          console.log(
            `✅ Ready fonts: ${readyFonts.length} / Expected: at least 1`,
          );

          if (readyFonts.length > 0) {
            console.log("🎉 Fonts are ready!");
            return true;
          }
        }
      } catch (error) {
        console.error("Polling error:", error);
      }

      if (attempt < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
    }

    console.log("⏰ Polling timeout reached");
    return false;
  };

  const handleFontFiles = async (files: FileList) => {
    let currentBrandId = brandId;

    if (!currentBrandId) {
      const brandName = formik.values.brand_name?.trim();
      const brandCountry = formik.values.brand_country?.trim();

      if (!brandName || !brandCountry) {
        toast.error("Please enter Brand Name & Country before uploading fonts");
        return;
      }

      try {
        const fd = new FormData();
        fd.append("brand_name", brandName);
        fd.append("brand_country", brandCountry);
        fd.append(
          "font_configuration",
          formik.values.font_configuration || "brand_fonts",
        );
        fd.append("title_fonts", formik.values.title_fonts);
        fd.append("subtitle_fonts", formik.values.subtitle_fonts);
        fd.append("body_text_fonts", formik.values.body_text_fonts);
        fd.append(
          "title_fonts_selected_from",
          formik.values.title_fonts === "design_default"
            ? ""
            : formik.values.title_fonts_selected_from || "",
        );
        fd.append(
          "subtitle_fonts_selected_from",
          formik.values.subtitle_fonts === "design_default"
            ? ""
            : formik.values.subtitle_fonts_selected_from || "",
        );
        fd.append(
          "body_text_fonts_selected_from",
          formik.values.body_text_fonts === "design_default"
            ? ""
            : formik.values.body_text_fonts_selected_from || "",
        );

        const createRes = await BrandApi.createBrand(fd);

        if (!createRes.success) {
          toast.error(createRes.error || "Failed to create brand");
          return;
        }

        const createdBrand = createRes.data?.data;
        currentBrandId = createdBrand?._id;

        if (!currentBrandId) {
          toast.error("Brand ID not returned");
          return;
        }

        setBrandId(currentBrandId);
        formik.setFieldValue("brand_id", currentBrandId);

        setSearchParams({ id: currentBrandId });

        console.log("Brand auto-created before font upload:", currentBrandId);
      } catch (err) {
        console.error("Brand auto-create error:", err);
        toast.error("Failed to create brand. Please try again.");
        return;
      }
    }

    setIsFontUploading(true);
    const formData = new FormData();

    Array.from(files).forEach((file) => {
      formData.append("fonts", file);
    });

    formData.append("brand_id", currentBrandId!);

    try {
      const res = await BrandApi.UploadFonts(formData, "brand");

      if (!res.success) {
        toast.error(res.error || "Failed to upload fonts");
        setIsFontUploading(false);
        setIsFontProcessing(false);
        return;
      }

      console.log("Upload response:", res.data);

      setIsFontUploading(false);
      setIsFontProcessing(false);

      await fetchCustomFonts(currentBrandId);

      toast.success("Fonts uploaded successfully!");

      (async () => {
        await new Promise((resolve) => setTimeout(resolve, 3000));

        let attempts = 0;
        const maxAttempts = 10;

        while (attempts < maxAttempts) {
          attempts++;
          console.log(
            `Background CDN check - attempt ${attempts}/${maxAttempts}`,
          );

          try {
            const checkRes = await BrandApi.GetFonts({
              fonts_for: "brand",
              brand_id: currentBrandId!,
            });

            if (checkRes.success && checkRes.data) {
              let arr: any[] = [];
              if (Array.isArray(checkRes.data)) arr = checkRes.data;
              else if (Array.isArray(checkRes.data?.docs))
                arr = checkRes.data.docs;
              else if (Array.isArray(checkRes.data?.data?.docs))
                arr = checkRes.data.data.docs;
              else if (Array.isArray(checkRes.data?.data))
                arr = checkRes.data.data;

              const allReady =
                arr.length > 0 &&
                arr.every((f: any) => {
                  if (
                    f.woff2_font_storage_key &&
                    f.woff2_font_storage_key.trim() !== ""
                  ) {
                    return true;
                  }
                  if (Array.isArray(f.variants) && f.variants.length > 0) {
                    return f.variants.some(
                      (v: any) =>
                        v.woff2_font_storage_key &&
                        v.woff2_font_storage_key.trim() !== "",
                    );
                  }
                  return false;
                });

              if (allReady) {
                console.log("All fonts ready on CDN!");
                await fetchCustomFonts(currentBrandId);
                break;
              }
            }
          } catch (err) {
            console.error("Background polling error:", err);
          }

          await new Promise((resolve) => setTimeout(resolve, 2000));
        }
      })();
    } catch (error) {
      toast.error("Upload failed.");
      console.error(error);
      setIsFontUploading(false);
      setIsFontProcessing(false);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const hexToRgb = (hex: string) => {
    const cleaned = hex.replace("#", "");
    const bigint = parseInt(cleaned, 16);

    return {
      r: (bigint >> 16) & 255,
      g: (bigint >> 8) & 255,
      b: bigint & 255,
    };
  };

  const normalizeColor = (color: any) => {
    if (!color) return null;

    if (typeof color === "object" && "r" in color) {
      return color;
    }

    if (typeof color === "string") {
      return hexToRgb(color);
    }

    return null;
  };

  const getLuminance = (r: number, g: number, b: number) => {
    const toLinear = (c: number) => {
      c /= 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    };

    const R = toLinear(r);
    const G = toLinear(g);
    const B = toLinear(b);

    return 0.2126 * R + 0.7152 * G + 0.0722 * B;
  };

  const getContrastRatio = (bg: any, text: any) => {
    const bgColor = normalizeColor(bg);
    const textColor = normalizeColor(text);

    if (!bgColor || !textColor) return null;

    const L1 = getLuminance(bgColor.r, bgColor.g, bgColor.b);
    const L2 = getLuminance(textColor.r, textColor.g, textColor.b);

    const lighter = Math.max(L1, L2);
    const darker = Math.min(L1, L2);

    return (lighter + 0.05) / (darker + 0.05);
  };

  const formik = useFormik({
    initialValues: {
      brand_name: "",
      brand_country: "",
      theme_colors: [],
      brand_logo: null,
      brandLogoUploadRecord: null,
      brandFonts: [],
      font_configuration: "brand_fonts",
      title_fonts: "design_default",
      subtitle_fonts: "design_default",
      body_text_fonts: "design_default",
      title_fonts_selected_from: null as any,
      subtitle_fonts_selected_from: null as any,
      body_text_fonts_selected_from: null as any,
      theme_colors_response: [],
      base_color_primary: null,
      base_color_secondary: null,
      text_color_primary: null,
      text_color_secondary: null,
    },
    validationSchema: Yup.object({
      brand_name: Yup.string().required("Brand name is required"),
      brand_country: Yup.string().required("Country is required"),
    }),
    validateOnMount: false,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      const errors = await formik.validateForm();

      const blockingErrors = Object.keys(errors).filter(
        (key) => key !== "primaryContrast" && key !== "secondaryContrast",
      );

      if (blockingErrors.length > 0) {
        toast.error("Please fix required fields before creating brand");
        return;
      }

      let currentBrandId = brandId;

      if (!currentBrandId) {
        const fd = new FormData();
        if (values.brand_name?.trim())
          fd.append("brand_name", values.brand_name);
        if (values.brand_country?.trim())
          fd.append("brand_country", values.brand_country);

        fd.append(
          "font_configuration",
          values.font_configuration || "brand_fonts",
        );
        fd.append("title_fonts", values.title_fonts);
        fd.append("subtitle_fonts", values.subtitle_fonts);
        fd.append("body_text_fonts", values.body_text_fonts);
        fd.append(
          "title_fonts_selected_from",
          values.title_fonts === "design_default"
            ? ""
            : values.title_fonts_selected_from || "",
        );
        fd.append(
          "subtitle_fonts_selected_from",
          values.subtitle_fonts === "design_default"
            ? ""
            : values.subtitle_fonts_selected_from || "",
        );
        fd.append(
          "body_text_fonts_selected_from",
          values.body_text_fonts === "design_default"
            ? ""
            : values.body_text_fonts_selected_from || "",
        );

        const createRes = await BrandApi.createBrand(fd);

        if (!createRes.success) {
          toast.error(createRes.error || "Failed to create brand");
          return;
        }

        const createdBrand = createRes.data?.data;
        currentBrandId = createdBrand?._id;

        if (!currentBrandId) {
          toast.error("Brand ID not returned from createBrand API");
          return;
        }

        setBrandId(currentBrandId);
      }

      const formData = new FormData();

      if (values.brand_name?.trim())
        formData.append("brand_name", values.brand_name);
      if (values.brand_country?.trim())
        formData.append("brand_country", values.brand_country);

      formData.append("font_configuration", values.font_configuration);
      formData.append("title_fonts", values.title_fonts);
      formData.append("subtitle_fonts", values.subtitle_fonts);
      formData.append("body_text_fonts", values.body_text_fonts);
      formData.append(
        "title_fonts_selected_from",
        values.title_fonts === "design_default"
          ? ""
          : values.title_fonts_selected_from || "",
      );
      formData.append(
        "subtitle_fonts_selected_from",
        values.subtitle_fonts === "design_default"
          ? ""
          : values.subtitle_fonts_selected_from || "",
      );
      formData.append(
        "body_text_fonts_selected_from",
        values.body_text_fonts === "design_default"
          ? ""
          : values.body_text_fonts_selected_from || "",
      );

      formData.append("status", "active");
      formData.append(
        "theme_colors_response",
        JSON.stringify(values.theme_colors?.slice(0, 4) || []),
      );

      if (values.base_color_primary)
        formData.append(
          "base_color_primary",
          JSON.stringify(values.base_color_primary),
        );
      if (values.base_color_secondary)
        formData.append(
          "base_color_secondary",
          JSON.stringify(values.base_color_secondary),
        );
      if (values.text_color_primary)
        formData.append(
          "text_color_primary",
          JSON.stringify(values.text_color_primary),
        );
      if (values.text_color_secondary)
        formData.append(
          "text_color_secondary",
          JSON.stringify(values.text_color_secondary),
        );

      const response = await BrandApi.updateBrandDetails(
        currentBrandId,
        formData,
      );

      if (response.success) {
        toast.success("Brand created successfully");
        navigate("/brand-list");
      } else {
        toast.error(response.error || "Failed to save brand");
      }
    },
  });

  useEffect(() => {
    const values = formik.values;
    const warnings: Record<string, string> = {};

    const rgbToHex = (r: number, g: number, b: number) => {
      return (
        "#" +
        [r, g, b]
          .map((x) => {
            const hex = Number(x).toString(16);
            return hex.length === 1 ? "0" + hex : hex;
          })
          .join("")
      );
    };

    const getLuminance = (r: number, g: number, b: number) => {
      const a = [r, g, b].map((v) => {
        v /= 255;
        return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
      });

      return 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2];
    };

    const getContrastRatio = (color1: string, color2: string) => {
      const hexToRgbObject = (hex: string) => {
        const cleaned = hex.replace("#", "");
        return {
          r: parseInt(cleaned.substring(0, 2), 16),
          g: parseInt(cleaned.substring(2, 4), 16),
          b: parseInt(cleaned.substring(4, 6), 16),
        };
      };

      const rgb1 = hexToRgbObject(color1);
      const rgb2 = hexToRgbObject(color2);

      const lum1 = getLuminance(rgb1.r, rgb1.g, rgb1.b);
      const lum2 = getLuminance(rgb2.r, rgb2.g, rgb2.b);

      const brightest = Math.max(lum1, lum2);
      const darkest = Math.min(lum1, lum2);

      return (brightest + 0.05) / (darkest + 0.05);
    };

    const safeHex = (color: any) => {
      if (!color) return null;

      if (color.rgb?.r != null) {
        return rgbToHex(color.rgb.r, color.rgb.g, color.rgb.b);
      }

      if (color.r != null) {
        return rgbToHex(color.r, color.g, color.b);
      }

      if (typeof color.hex === "string") {
        return color.hex;
      }

      return null;
    };

    const basePrimary = safeHex(values.base_color_primary);
    const textPrimary = safeHex(values.text_color_primary);

    if (basePrimary && textPrimary) {
      const contrast = getContrastRatio(basePrimary, textPrimary);
      if (contrast < 4.5) {
        warnings.primaryContrast =
          "Primary text color is difficult to read on Base Primary color.";
      }
    }

    const baseSecondary = safeHex(values.base_color_secondary);
    const textSecondary = safeHex(values.text_color_secondary);

    if (baseSecondary && textSecondary) {
      const contrast = getContrastRatio(baseSecondary, textSecondary);
      if (contrast < 4.5) {
        warnings.secondaryContrast =
          "Secondary text color is difficult to read on Base Secondary color.";
      }
    }

    setContrastWarnings(warnings);
  }, [
    formik.values.base_color_primary,
    formik.values.base_color_secondary,
    formik.values.text_color_primary,
    formik.values.text_color_secondary,
  ]);

  const colorErrors: Record<string, string> = {};
  const submitErrors: Record<string, string> = {};

  Object.entries(formik.errors).forEach(([key, value]) => {
    if (key === "primaryContrast" || key === "secondaryContrast") {
      colorErrors[key] = value as string;
    } else {
      submitErrors[key] = value as string;
    }
  });

  useEffect(() => {
    console.log("Formik errors:", formik.errors);
  }, [formik.errors]);

  const handleFontSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFontFiles(e.target.files);
    }
  };

  const handleFontDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFontFiles(e.dataTransfer.files);
    }
  };

  const handleRemoveFont = async (id: string) => {
    try {
      const res = await BrandApi.RemoveFont(id, "brand");

      if (!res.success) {
        toast.error(res.error || "Failed to remove font");
        return;
      }

      formik.setFieldValue(
        "brandFonts",
        formik.values.brandFonts.filter((font: any) => font.id !== id),
      );

      toast.success("Font removed successfully!");
      await fetchCustomFonts(brandId);
    } catch (error) {
      toast.error("Failed to remove font");
      console.error(error);
    }
  };

  const getFontById = (id: string) => {
    if (!id || id === "design_default") return null;
    return (
      customFonts.find((font) => font.value === id) ||
      systemFonts.find((font) => font.value === id) ||
      null
    );
  };

  const isBrandLogoUploaded = !!formik.values?.brand_logo;
  const isPreviewReady = !!formik.values?.brand_logo;

  useEffect(() => {
    return () => {
      sessionStorage.removeItem("draft_brand_id");
    };
  }, []);

  const COUNTRY_OPTIONS = [
    { value: "australia", label: "Australia" },
    { value: "new_zealand", label: "New Zealand" },
  ];

  const hasBlockingErrors = Object.keys(formik.errors).some(
    (key) => key !== "primaryContrast" && key !== "secondaryContrast",
  );

  const handleSystemFontSelected = (font: FontItem) => {
    setSystemFonts((prev) => {
      const alreadyExists = prev.some((f) => f.value === font.value);
      if (alreadyExists) return prev;
      return [...prev, font];
    });
  };

  return (
    <ProtectedComponent>
      <NavigationLayout>
        <div className="create-brand-wrapper">
          <div
            className="propertie-view-back-btn"
            onClick={() => {
              sessionStorage.removeItem("draft_brand_id");
              navigate("/brand-list");
            }}
          >
            <div className="back-icon">
              <BackArrowIcon />
            </div>
            <p className="bakc-text-contain">Back to Brand list</p>
          </div>
          <h2 className="page-title">Create Brand</h2>

          <div className="grammar-section brand-basic-info">
            <div className="brand-basic-grid">
              <div className="input-wrapper">
                <label>Brand Name*</label>
                <input
                  type="text"
                  name="brand_name"
                  placeholder="Enter brand name"
                  value={formik.values.brand_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="brand-input"
                />
              </div>

              <div className="input-wrapper">
                <label>Country*</label>

                <FormControl fullWidth>
                  <Select
                    name="brand_country"
                    value={formik.values.brand_country}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    displayEmpty
                    className="brand-country-select"
                    renderValue={(selected) => {
                      if (!selected) {
                        return (
                          <span className="placeholder">Select country</span>
                        );
                      }

                      const option = COUNTRY_OPTIONS.find(
                        (item) => item.value === selected,
                      );

                      return option ? option.label : selected;
                    }}
                  >
                    {COUNTRY_OPTIONS.map((country) => (
                      <MenuItem key={country.value} value={country.value}>
                        {country.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </div>
          </div>

          <div className="create-brand-container">
            <div className="create-brand-left">
              <div className="grammar-section">
                <div className="section-title">Brand/Franchise Logo*</div>
                <div className="brand-section-desc brand-desc">
                  Upload the Brand’s official logo using the specifications
                  below.
                  <ul>
                    <li>
                      <span className="office-logo">Dimensions:</span>{" "}
                      Recommended size is 510 x 96 pixels.
                    </li>
                    <li>
                      <span className="office-logo">Background:</span> Must be a
                      solid background (no transparency) for optimal color
                      generation.
                    </li>
                    <li>
                      <span className="office-logo">Quality:</span> Please
                      provide a high-resolution file to ensure your branding
                      remains crisp on social media posts.
                    </li>
                    <li>Accepted files are PNG, JPG, or JPEG.</li>
                  </ul>
                </div>
                <BrandProfileLogo
                  formik={formik}
                  brandId={brandId}
                  setBrandId={setBrandId}
                />
              </div>

              <div className="grammar-section">
                <div className="section-title">Brand/Franchisee Colors*</div>
                <div className="section-desc brand-desc">
                  These colors are automatically extracted from the brand logo
                  to ensure visual consistency across all marketing assets and
                  connected agency profiles.
                </div>
                {isBrandLogoUploaded ? (
                  <ThemePreferences
                    formik={formik}
                    isLocked={false}
                    originalColors={originalColors}
                  />
                ) : (
                  <div className="placeholder-box">
                    <p>Upload Brand/Franchisee logo to generate the colors</p>
                  </div>
                )}
              </div>

              <ThemeErrorSummary errors={contrastWarnings} />

              <div className="scrollable-form-body">
                <div className="typography-form-group">
                  <label className="typography-group-label">
                    Upload Brand Fonts
                  </label>

                  <p className="typography-group-desc">
                    Official brand fonts used to generate marketing collateral
                    and maintain cross-platform visual consistency.
                  </p>

                  <p className="typography-group-sub-desc">
                    Official font family files (including all weight variants)
                    used to standardize brand styling across the platform.
                  </p>

                  <div
                    className={`upload-box ${isFontUploading ? "uploading" : ""}`}
                    onDrop={!isFontUploading ? handleFontDrop : undefined}
                    onDragOver={(e) => e.preventDefault()}
                    onClick={() =>
                      !isFontUploading && fileInputRef.current?.click()
                    }
                  >
                    <input
                      type="file"
                      hidden
                      multiple
                      ref={fileInputRef}
                      accept=".zip,.rar,.ttf,.otf,.woff2"
                      disabled={isFontUploading}
                      onChange={handleFontSelect}
                    />

                    <div className="icon-wrapper">
                      {isFontUploading ? (
                        <div className="black-color-circular-progress">
                          <CircularProgress size={28} />
                        </div>
                      ) : (
                        <Upload />
                      )}
                    </div>

                    <p className="upload-text">
                      {isFontUploading
                        ? "Uploading & processing fonts..."
                        : "Drag & Drop your font files here or Click to Browse"}
                    </p>
                  </div>

                  <p className="support-text">
                    Supported: .zip, .rar, .ttf, .otf, .woff2 (Multiple
                    selection allowed)
                  </p>

                  {formik.values.brandFonts.length > 0 && (
                    <div className="uploaded-files-list">
                      {formik.values.brandFonts.map((font: any, idx) => (
                        <div key={idx} className="file-chip font-family-chip">
                          <img
                            src="/images/font-icon.png"
                            alt="font-icon"
                            className="font-file-icon"
                          />
                          <div className="font-info">
                            <span
                              className="font-family-name"
                              style={{
                                fontFamily: font.family || "inherit",
                                fontStyle: "normal",
                                fontWeight: 400,
                              }}
                            >
                              {font.family}
                            </span>
                            <span className="available-weights-label">
                              Available weights
                            </span>
                            <span className="available-weights-list">
                              {font.availableWeights?.join(", ") || "-"}
                            </span>
                          </div>
                          <button
                            type="button"
                            className="remove-font-btn"
                            disabled={isFontUploading}
                            onClick={() => handleRemoveFont(font.id)}
                            title={`Remove ${font.family} family and all its variants`}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="typography-form-group">
                <label className="typography-group-label">Typography</label>
                <p className="typography-group-desc">
                  Configure fonts to match your agency's design specifications.
                </p>

                <div className="typography-grid">
                  <div className="select-wrapper">
                    <label>Title</label>
                    <div
                      className="custom-select-box"
                      onClick={() => setOpenTitleFontModal(true)}
                    >
                      <div
                        className="select-render"
                        style={{
                          fontFamily:
                            getFontById(formik.values.title_fonts)?.family ||
                            "inherit",
                        }}
                      >
                        <SearchIcon />
                        {getFontById(formik.values.title_fonts)?.label ||
                          "Design default"}
                      </div>
                    </div>

                    <FontSelectorModal
                      open={openTitleFontModal}
                      onClose={() => setOpenTitleFontModal(false)}
                      value={formik.values.title_fonts}
                      onChange={(val, font, source) => {
                        formik.setFieldValue("title_fonts", val);
                        formik.setFieldValue(
                          "title_fonts_selected_from",
                          source || null,
                        );
                      }}
                      customFonts={customFonts}
                      // systemFonts={systemFonts}
                      customFontSectionLabel={customFontSectionLabel}
                      brandId={brandId || undefined}
                      fontsFor="brand"
                      onSystemFontSelected={handleSystemFontSelected}
                    />
                  </div>

                  <div className="select-wrapper">
                    <label>Subtitle</label>
                    <div
                      className="custom-select-box"
                      onClick={() => setOpenSubtitleFontModal(true)}
                    >
                      <div
                        className="select-render"
                        style={{
                          fontFamily:
                            getFontById(formik.values.subtitle_fonts)?.family ||
                            "inherit",
                        }}
                      >
                        <SearchIcon />
                        {getFontById(formik.values.subtitle_fonts)?.label ||
                          "Design default"}
                      </div>
                    </div>

                    <FontSelectorModal
                      open={openSubtitleFontModal}
                      onClose={() => setOpenSubtitleFontModal(false)}
                      value={formik.values.subtitle_fonts}
                      onChange={(val, font, source) => {
                        formik.setFieldValue("subtitle_fonts", val);
                        formik.setFieldValue(
                          "subtitle_fonts_selected_from",
                          source || null,
                        );
                      }}
                      customFonts={customFonts}
                      // systemFonts={systemFonts}
                      brandId={brandId || undefined}
                      fontsFor="brand"
                      customFontSectionLabel={customFontSectionLabel}
                      onSystemFontSelected={handleSystemFontSelected}
                    />
                  </div>
                </div>

                <div className="select-wrapper full-width-select">
                  <label>Body text</label>
                  <div
                    className="custom-select-box"
                    onClick={() => setOpenBodyFontModal(true)}
                  >
                    <div
                      className="select-render"
                      style={{
                        fontFamily:
                          getFontById(formik.values.body_text_fonts)?.family ||
                          "inherit",
                      }}
                    >
                      <SearchIcon />
                      {getFontById(formik.values.body_text_fonts)?.label ||
                        "Design default"}
                    </div>
                  </div>

                  <FontSelectorModal
                    open={openBodyFontModal}
                    onClose={() => setOpenBodyFontModal(false)}
                    value={formik.values.body_text_fonts}
                    onChange={(val, font, source) => {
                      formik.setFieldValue("body_text_fonts", val);
                      formik.setFieldValue(
                        "body_text_fonts_selected_from",
                        source || null,
                      );
                    }}
                    customFonts={customFonts}
                    // systemFonts={systemFonts}
                    brandId={brandId || undefined}
                    fontsFor="brand"
                    customFontSectionLabel={customFontSectionLabel}
                    onSystemFontSelected={handleSystemFontSelected}
                  />
                </div>
              </div>

              {formik.submitCount > 0 &&
                Object.keys(submitErrors).length > 0 && (
                  <ErrorSummary errors={submitErrors} />
                )}

              <div className="button-group">
                <button className="btn-cancel">Cancel</button>
                <button
                  type="button"
                  className="btn-primary"
                  disabled={hasBlockingErrors}
                  onClick={() => formik.handleSubmit()}
                >
                  Create brand
                </button>
              </div>
            </div>

            <div className="right-column">
              <div className="grammar-section">
                <div className="section-title">
                  Preview Post with Brand Info
                </div>
                <div className="section-desc right-grammer-desc">
                  A live look at your customized marketing content, styled with
                  your agency’s unique brand identity.
                </div>
                <div className="preview-wrapper">
                  <div className="preview-card">
                    {isPreviewReady ? (
                      <img
                        src="/images/brand_grammer_sample.png"
                        alt="Post Preview"
                        className="live-preview-img"
                      />
                    ) : (
                      <div className="empty-preview-content">
                        <img
                          src="/icons/pending-icon-2.png"
                          alt="Waiting Icon"
                          className="waiting-icon"
                        />
                        <p>
                          Upload brand logo and brand fonts to generate the
                          preview
                        </p>
                      </div>
                    )}
                  </div>
                  {isPreviewReady && (
                    <div className="preview-actions">
                      <button
                        type="button"
                        onClick={() => setShowMobilePreview(true)}
                      >
                        Preview
                      </button>
                      <button type="button">Download</button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div
              className={`mobile-bottom-drawer ${isDrawerOpen ? "open" : ""}`}
            >
              <div
                className="drawer-header"
                onClick={() => setIsDrawerOpen(true)}
              >
                <div className="drawer-handle-line"></div>
                <span className="drawer-title">Preview post</span>
              </div>

              <div className="drawer-content">
                <div className="drawer-scroll-area">
                  <div className="mobile-preview-card">
                    <div className="preview-text-info">
                      <h3>Preview Post with Brand Info</h3>
                      <p>
                        A live look at your customized marketing content, styled
                        with your agency’s unique brand identity
                      </p>
                    </div>

                    {isPreviewReady ? (
                      <img
                        src="/images/brand_grammer_sample.png"
                        alt="Post Preview"
                        className="live-preview-img"
                      />
                    ) : (
                      <div className="empty-preview-content">
                        <img
                          src="/icons/pending-icon-2.png"
                          alt="Waiting Icon"
                          className="waiting-icon"
                        />
                        <p>
                          Upload brand logo and brand fonts to generate the
                          preview
                        </p>
                      </div>
                    )}

                    {isPreviewReady && (
                      <div className="drawer-actions">
                        <button type="button" className="btn-preview">
                          Preview
                        </button>
                        <button type="button" className="btn-download">
                          Download
                        </button>
                      </div>
                    )}

                    <button
                      className="close-drawer-btn"
                      onClick={() => setIsDrawerOpen(false)}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Modal
          open={isFontUploading || isFontProcessing}
          onClose={() => {}}
          disableEscapeKeyDown
          hideBackdrop={false}
        >
          <Box className="font-uploading-modal">
            <div className="font-uploading-spinner"></div>

            <div className="font-uploading-texts">
              <p className="font-uploading-title">Uploading fonts...</p>
              <p className="font-uploading-subtitle">
                Please wait while we process your fonts.
              </p>
            </div>
          </Box>
        </Modal>
      </NavigationLayout>
    </ProtectedComponent>
  );
};

export default CreateBrand;
